import { track } from "./analytics";

/**
 * Field measurement for the three numbers that decide whether the site feels fast:
 * layout shift, the largest paint, and the first one.
 *
 * It exists because the last round of CLS work was diagnosis by reading code. The
 * dashboards said 62% of visits were in the "poor" band and named `html>body` as the
 * culprit, which is true of any full-document relayout and points at nothing in
 * particular. Cloudflare's and Google's field data are both four-week rolling
 * windows, so a fix and its verdict are a month apart — and a fix that does nothing
 * is indistinguishable from one that has not landed yet.
 *
 * This reports the same measurement per page load, within minutes, with the element
 * that actually moved. Nothing here is personal: a normalised path, a handful of
 * numbers, and a CSS-ish selector for the node the browser blamed.
 */

const GOOD_CLS = 0.1;
const POOR_CLS = 0.25;

/** A shift belongs to the current session window while it is within a second of the
    last shift and five seconds of the first. CLS is the worst window, not the sum —
    the same definition Chrome reports, so the numbers can be compared. */
const SESSION_GAP_MS = 1_000;
const SESSION_MAX_MS = 5_000;

type LayoutShiftSource = { node?: Node | null };
type LayoutShift = PerformanceEntry & {
  value: number;
  hadRecentInput: boolean;
  sources?: LayoutShiftSource[];
};

/**
 * Enough of a selector to find the element again, and no more. `main.play-page >
 * div.page-notes` is the answer to "what moved"; the full ancestor chain is not, and
 * it would be the one field here long enough to carry something unintended.
 */
function describe(node: Node | null | undefined): string {
  if (!node || !(node instanceof Element)) return "";
  const name = (element: Element): string => {
    const tag = element.tagName.toLowerCase();
    if (element.id) return `${tag}#${element.id}`;
    const first = element.classList.item(0);
    return first ? `${tag}.${first}` : tag;
  };
  const parent = node.parentElement;
  return (parent ? `${name(parent)}>` : "") + name(node);
}

/** Ten thousand dated archive pages are one page as far as a performance report is
    concerned, and one report per date would be a report about nothing. */
function normalisePath(pathname: string): string {
  return pathname.replace(/\/\d{4}-\d{2}-\d{2}$/, "/:date");
}

function bucket(cls: number): string {
  if (cls <= GOOD_CLS) return "good";
  if (cls <= POOR_CLS) return "needs-improvement";
  return "poor";
}

function navigationType(): string {
  const entry = performance.getEntriesByType("navigation")[0] as PerformanceNavigationTiming | undefined;
  return entry?.type ?? "unknown";
}

/** The events endpoint keeps the first twelve properties of an event and silently
    drops the rest, so staying under this is not a matter of taste — a thirteenth
    field would arrive as a column of nulls nobody would think to question. */
export const MAX_EVENT_PROPERTIES = 12;

export type VitalsSample = {
  pathname: string;
  cls: number;
  shiftCount: number;
  largestShift: number;
  largestSource: string;
  largestAt: number;
  lcp: number;
  fcp: number;
  viewportWidth: number;
  navigation: string;
};

/** Separated from the measuring so the shape of a report can be checked without a
    browser, and rounded here so two visitors on the same page produce comparable
    rows rather than sixteen digits of floating point each. */
export function vitalsProperties(sample: VitalsSample): Record<string, string | number> {
  return {
    path: normalisePath(sample.pathname),
    cls: Math.round(sample.cls * 1000) / 1000,
    cls_bucket: bucket(sample.cls),
    shift_count: sample.shiftCount,
    largest_shift: Math.round(sample.largestShift * 1000) / 1000,
    largest_source: sample.largestSource.slice(0, 60),
    largest_at_ms: Math.round(sample.largestAt / 100) * 100,
    lcp_ms: Math.round(sample.lcp),
    fcp_ms: Math.round(sample.fcp),
    viewport_w: sample.viewportWidth,
    nav_type: sample.navigation,
  };
}

export function reportWebVitals(): void {
  if (typeof PerformanceObserver !== "function") return;

  let clsWorst = 0;
  let sessionValue = 0;
  let sessionStart = 0;
  let sessionLast = 0;
  let shiftCount = 0;
  let largestValue = 0;
  let largestSource = "";
  let largestAt = 0;
  let lcp = 0;

  const observe = (type: string, handler: (entries: PerformanceEntry[]) => void): void => {
    try {
      new PerformanceObserver((list) => handler(list.getEntries())).observe({ type, buffered: true });
    } catch {
      // An engine without this entry type reports nothing rather than breaking the page.
    }
  };

  observe("layout-shift", (entries) => {
    for (const entry of entries as LayoutShift[]) {
      // A shift the player asked for — a tap that opened the menu — is not a defect.
      if (entry.hadRecentInput) continue;
      shiftCount += 1;

      const startsNewSession =
        sessionValue === 0 ||
        entry.startTime - sessionLast > SESSION_GAP_MS ||
        entry.startTime - sessionStart > SESSION_MAX_MS;
      if (startsNewSession) {
        sessionValue = entry.value;
        sessionStart = entry.startTime;
      } else {
        sessionValue += entry.value;
      }
      sessionLast = entry.startTime;
      if (sessionValue > clsWorst) clsWorst = sessionValue;

      // The single worst jump is what a fix has to target, so it is reported
      // alongside the score rather than averaged into it.
      if (entry.value > largestValue) {
        largestValue = entry.value;
        largestAt = entry.startTime;
        largestSource = describe(entry.sources?.[0]?.node) || "unattributed";
      }
    }
  });

  observe("largest-contentful-paint", (entries) => {
    const last = entries[entries.length - 1];
    if (last) lcp = last.startTime;
  });

  let sent = false;
  const send = (): void => {
    if (sent) return;
    sent = true;
    const fcp = performance.getEntriesByName("first-contentful-paint")[0]?.startTime ?? 0;
    const properties = vitalsProperties({
      pathname: window.location.pathname,
      cls: clsWorst,
      shiftCount,
      largestShift: largestValue,
      largestSource,
      largestAt,
      lcp,
      fcp,
      viewportWidth: window.innerWidth,
      navigation: navigationType(),
    });
    track("web_vitals", properties, { beacon: true });
  };

  // Hidden is the only moment a page is reliably still alive to report from: an
  // unload handler is not, and a phone that switches apps never fires one.
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") send();
  });
  window.addEventListener("pagehide", send);
}
